import { Capacitor } from '@capacitor/core';
import { PluginListenerHandle } from '@capacitor/core';
import http from './http/index';
import { getAriaPath } from '../utils/ariaUrls';
import { ARIA_OPEN_CONNECTIONS_MANAGER_EVENT } from '../constants/aria';
import { isImpersonationSession } from '../utils/impersonation';

/** Push (FCM) is enabled for any Capacitor native build — Portal and Ask Aria standalone. */
const isNativePushCapable = Capacitor.isNativePlatform();

interface FirebaseMessagingPlugin {
    requestPermissions(): Promise<{ receive: string }>;
    checkPermissions(): Promise<{ receive: string }>;
    getToken(): Promise<{ token: string }>;
    addListener(eventName: string, listenerFunc: (event: any) => void): Promise<PluginListenerHandle>;
    removeAllListeners(): Promise<void>;
}

let FirebaseMessaging: FirebaseMessagingPlugin | null = null;

if (isNativePushCapable) {
    try {
        const module = require('@capacitor-firebase/messaging');
        FirebaseMessaging = module.FirebaseMessaging;
    } catch (err) {
        console.error('[Push] Failed to load FirebaseMessaging plugin:', err);
    }
}

const TOKEN_KEY = 'pushToken';
const REGISTERED_FOR_USER_KEY = 'pushRegisteredForUser';

/**
 * Check current permission status without showing a dialog
 */
async function getCurrentPermission(): Promise<'granted' | 'denied' | 'prompt'> {
    if (!FirebaseMessaging) return 'denied';
    const { receive } = await FirebaseMessaging.checkPermissions();
    return receive as 'granted' | 'denied' | 'prompt';
}




/**
 * Initialize push notifications on app start
 */
export async function initPushNotifications(): Promise<void> {
    if (!isNativePushCapable || !FirebaseMessaging) return;

    try {
        const currentPermission = await getCurrentPermission();

        if (currentPermission === 'denied') {
            clearPushData();
            return;
        }

        if (currentPermission === 'granted') {
            const { token } = await FirebaseMessaging.getToken();
            const oldToken = localStorage.getItem(TOKEN_KEY);
            const lastUpdate = localStorage.getItem('tokenLastUpdated');
            const userStr = localStorage.getItem('user');
            
            // Store token regardless
            localStorage.setItem(TOKEN_KEY, token);
            
            // Check if user is logged in
            if (userStr) {
                const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
                const needsUpdate = 
                    token !== oldToken || // Token changed
                    !lastUpdate || // First time ever
                    Date.now() - parseInt(lastUpdate) > THIRTY_DAYS; // Older than 30 days
                
                if (needsUpdate) {
                    try {
                        const userData = JSON.parse(userStr);
                        const userId = userData?.user?.userId || userData?.userId;
                        
                        if (userId) {
                            await http.patch('/api/push/token', { token });
                            localStorage.setItem('tokenLastUpdated', Date.now().toString());
                        }
                    } catch (err) {
                        console.error('[Push] Failed to update token:', err);
                    }
                }
            }
        }
    } catch (err) {
        console.error('[Push] Initialization error:', err);
    }
}
/**
 * Register the push token with the backend after user logs in.
 * This is where we actually request permission if needed.
 */
export async function registerPushTokenAfterLogin(): Promise<void> {
    if (!isNativePushCapable || !FirebaseMessaging) return;

    // The device belongs to the admin, not the account being viewed. Registering here would
    // move the token onto that account and send their notifications to this handset.
    if (isImpersonationSession()) return;

    try {
        // Check current permission
        let currentPermission = await getCurrentPermission();
        
        // Handle each permission state
        if (currentPermission === 'denied') {
            clearPushData();
            return;
        }
        
        if (currentPermission === 'prompt') {
            // NOW we show the dialog - after user is logged in
            const { receive } = await FirebaseMessaging.requestPermissions();
            if (receive !== 'granted') {
                clearPushData();
                return;
            }
            // Permission granted, update currentPermission for next steps
            currentPermission = 'granted';
        }

        // If we get here, permission is granted (either was already or just got it)
        if (currentPermission === 'granted') {
            // Get user ID
            const userStr = localStorage.getItem('user');
            if (!userStr) return;
            
            let userId: string;
            try {
                const userData = JSON.parse(userStr);
                if (userData?.user?.userId) {
                    userId = userData.user.userId;
                } else {
                    return;
                }
            } catch {
                return;
            }

            // Skip if already registered for this user
            const alreadyRegisteredFor = localStorage.getItem(REGISTERED_FOR_USER_KEY);
            if (alreadyRegisteredFor === userId) return;

            // Get token from storage or fetch fresh
            let token = localStorage.getItem(TOKEN_KEY);
            if (!token || token.length < 20) {
                try {
                    const result = await FirebaseMessaging.getToken();
                    token = result.token;
                    if (token) {
                        localStorage.setItem(TOKEN_KEY, token);
                    }
                } catch (err) {
                    console.error('[Push] Could not get token:', err);
                    return;
                }
            }

            if (!token || token.length < 20) return;

            await registerToken(token, userId);
        }
        
    } catch (err) {
        console.error('[Push] Failed to register token after login:', err);
    }
}

/**
 * Send the token to backend for the current user
 */
async function registerToken(token: string, userId: string): Promise<void> {
    try {
        await http.patch('/api/push/token', { token });
        localStorage.setItem(REGISTERED_FOR_USER_KEY, userId);
    } catch (err) {
        console.error('[Push] Failed to register token:', err);
        throw err;
    }
}
/**
 * Remove the push token from backend on logout
 */
export async function unregisterPushToken(): Promise<void> {
    if (!isNativePushCapable) return;
    
    try {
        await http.delete('/api/push/token');
        clearPushData();
    } catch (err) {
        console.error('[Push] Failed to unregister token:', err);
        clearPushData(); // still clear local data even if backend fails
    }
}

/**
 * Clear all push notification data from storage
 */
function clearPushData(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REGISTERED_FOR_USER_KEY);
}

function navigateForConnectionAcceptedPush(navigate: (path: string) => void): void {
    navigate(getAriaPath('/'));
    window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent(ARIA_OPEN_CONNECTIONS_MANAGER_EVENT));
    }, 400);
}

/**
 * Handle notification taps and navigation
 */
export function handleNotificationTap(
    navigate: (path: string) => void
): () => void {
    if (!isNativePushCapable || !FirebaseMessaging) return () => {};

    // Callers re-run this whenever `navigate` changes identity (every route change). addListener
    // resolves async, so a cleanup can run before a handle exists; `disposed` removes it on arrival
    // instead of leaking a duplicate listener.
    let disposed = false;
    let fcmListenerHandle: PluginListenerHandle | null = null;
    let localListenerHandle: PluginListenerHandle | null = null;

    const routeFromPushData = (data: Record<string, string> | undefined) => {
        if (!data?.type) return;
        if (data.type === 'assessment_complete' && data.personId) {
            navigate(`/people/${data.personId}`);
            return;
        }
        if (data.type === 'connection_accepted') {
            navigateForConnectionAcceptedPush(navigate);
            return;
        }
        if (data.type === 'developer_update') {
            navigate(
                data.notificationId
                    ? `/notifications?open=${encodeURIComponent(data.notificationId)}`
                    : '/notifications'
            );
            return;
        }
        if (data.type === 'aria_daily') {
            navigate(getAriaPath('/'));
            return;
        }
        if (data.type === 'aria_response_complete') {
            if (data.urlToken) {
                const chatPath = `/m/${data.urlToken}`;
                navigate(
                    process.env.REACT_APP_TARGET === 'portal'
                        ? chatPath
                        : getAriaPath(chatPath)
                );
            } else {
                navigate(
                    process.env.REACT_APP_TARGET === 'portal'
                        ? '/'
                        : getAriaPath('/')
                );
            }
            return;
        }
    };

    // Handle taps on FCM notifications (background/killed)
    FirebaseMessaging.addListener(
        'notificationActionPerformed',
        (event: any) => {
            const data = event.notification?.data as Record<string, string> | undefined;
            routeFromPushData(data);
        }
    ).then((handle: PluginListenerHandle) => {
        if (disposed) handle.remove();
        else fcmListenerHandle = handle;
    });

    // Handle taps on LocalNotifications (foreground)
    try {
        const { LocalNotifications } = require('@capacitor/local-notifications');
        LocalNotifications.addListener(
            'localNotificationActionPerformed',
            (event: any) => {
                const data = event.notification?.extra as Record<string, string> | undefined;
                routeFromPushData(data);
            }
        ).then((handle: PluginListenerHandle) => {
            if (disposed) handle.remove();
            else localListenerHandle = handle;
        });
    } catch (err) {
        console.error('[Push] Failed to add local notification tap listener:', err);
    }

    return () => {
        disposed = true;
        fcmListenerHandle?.remove();
        localListenerHandle?.remove();
    };
}
/**
 * Fetches the current user's push notification preferences from the backend.
 */
export async function getPushPreferences(): Promise<{
    assessmentComplete: boolean;
    connectionAccepted: boolean;
    ariaDailyDigest: boolean;
    ariaResponseComplete: boolean;
    developerUpdate: boolean;
    hasToken: boolean;
}> {
    try {
        const response = await http.get<{
            success: boolean;
            data: {
                assessmentComplete: boolean;
                connectionAccepted: boolean;
                ariaDailyDigest: boolean;
                ariaResponseComplete: boolean;
                developerUpdate: boolean;
                hasToken: boolean;
            };
        }>('/api/push/preferences');
        return response.data.data;
    } catch (err) {
        console.error('[Push] Failed to fetch preferences:', err);
        return {
            assessmentComplete: false,
            connectionAccepted: true,
            ariaDailyDigest: false,
            ariaResponseComplete: true,
            developerUpdate: true,
            hasToken: false,
        };
    }
}

/**
 * Updates the user's push notification preferences on the backend.
 */
export async function updatePushPreferences(prefs: {
    assessmentComplete?: boolean;
    connectionAccepted?: boolean;
    ariaDailyDigest?: boolean;
    ariaResponseComplete?: boolean;
    developerUpdate?: boolean;
}): Promise<void> {
    await http.patch('/api/push/preferences', prefs);
}

/**
 * Listen for foreground FCM messages and display them via LocalNotifications.
 * Android only — iOS shows foreground notifications automatically.
 * Call once at app startup.
 */
export function initForegroundNotificationListener(): void {
    if (!isNativePushCapable || !FirebaseMessaging) return;

    // iOS shows foreground notifications automatically via FCM
    // Android needs manual LocalNotifications scheduling
    if (Capacitor.getPlatform() !== 'android') return;

    try {
        FirebaseMessaging.addListener('notificationReceived', async (event: any) => {
            const n = event.notification;
            const title = n?.title ?? n?.data?.title ?? 'Notification';
            const body = n?.body ?? n?.data?.body ?? '';

            try {
                const { LocalNotifications } = require('@capacitor/local-notifications');

                await LocalNotifications.createChannel({
                    id: 'aptive_high',
                    name: 'General Notifications',
                    importance: 5,
                    visibility: 1,
                    sound: 'default',
                    vibration: true,
                    lights: true,
                });

                const perm = await LocalNotifications.checkPermissions();

                if (perm.display !== 'granted') {
                    await LocalNotifications.requestPermissions();
                }

                await LocalNotifications.schedule({
                    notifications: [{
                        id: Math.floor(Math.random() * 100000),
                        title,
                        body,
                        smallIcon: 'ic_stat_notification',
                        channelId: 'aptive_high',
                        extra: {
                            type: n?.data?.type ?? '',
                            personId: n?.data?.personId ?? '',
                            connectionId: n?.data?.connectionId ?? '',
                            notificationId: n?.data?.notificationId ?? '',
                            urlToken: n?.data?.urlToken ?? '',
                        },
                    }],
                });

            } catch (err: any) {
                console.error('[Push] LocalNotifications error:', err?.message ?? err);
            }
        });
    } catch (err) {
        console.error('[Push] Failed to init foreground notification listener:', err);
    }
}

/**
 * Initialize token refresh listener
 * This updates the backend when Firebase issues a new token
 */
export function initTokenRefreshListener(): void {
    if (!isNativePushCapable || !FirebaseMessaging) return;

    try {
        FirebaseMessaging.addListener('tokenReceived', async (event: { token: string }) => {
            const newToken = event.token;
            const oldToken = localStorage.getItem('pushToken');
            const user = localStorage.getItem('user');
            
            localStorage.setItem('pushToken', newToken);
            
            if (user && newToken !== oldToken) {
                try {
                    const userData = JSON.parse(user);
                    const userId = userData?.user?.userId || userData?.userId;
                    
                    if (userId) {
                        await http.patch('/api/push/token', { token: newToken });
                        localStorage.setItem('tokenLastUpdated', Date.now().toString());
                    }
                } catch (err) {
                    console.error('Failed to send refreshed token:', err);
                }
            }
        });
    } catch (err) {
        console.error('Failed to init token refresh listener:', err);
    }
}